import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';

import { Tag, tags as t } from '@lezer/highlight';

export const filterTag = Tag.define();

// TODO: some day we have to define all colors in css variables if possible

// ==================== DARK THEME ====================

const darkUITheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#282a36',
      color: '#f8f8f2',
    },
    '.cm-content': {
      caretColor: '#f8f8f0',
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: '#f8f8f0',
    },
    '.cm-activeLine': {
      backgroundColor: 'unset',
    },
    '&.cm-focused .cm-selectionBackground, & .cm-line::selection, & .cm-selectionLayer .cm-selectionBackground, .cm-content ::selection':
      {
        background: 'rgba(255, 255, 255, 0.1) !important',
      },
    '& .cm-selectionMatch': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    '.cm-gutters': {
      backgroundColor: '#282a36',
      color: '#6D8A88',
      borderRight: 'transparent',
    },
    '.cm-activeLineGutter': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '.cm-matchingBracket': {
      backgroundColor: 'rgba(255, 255, 0, 0.5)',
      outline: '1px solid cyan',
    },
    '&.cm-focused .cm-matchingBracket': {
      backgroundColor: 'rgba(255, 255, 0, 0.5)',
      outline: '1px solid cyan',
    },
    '.cm-query-dimension': {
      color: '#cccccc !important',
      fontWeight: 'bold',
    },
    '.cm-query-dimension span': {
      color: '#cccccc !important',
      fontWeight: 'bold',
    },
    '.cm-query-property-name': {
      color: '#66d9ef',
      fontWeight: 'bold',
    },
    '.cm-query-property-name span': {
      color: '#66d9ef',
      fontWeight: 'bold',
    },
  },
  { dark: true },
);

const darkSyntaxHighlighting = syntaxHighlighting(
  HighlightStyle.define([
    { tag: t.comment, color: '#6272a4' },
    { tag: t.lineComment, color: '#6272a4' },
    {
      tag: t.blockComment,
      color: '#0080ff',
      fontWeight: 'bold',
      fontStyle: 'italic',
    },
    { tag: t.string, color: '#f1fa8c' },
    { tag: t.atom, color: '#bd93f9' },
    { tag: t.meta, color: '#f8f8f2' },
    {
      tag: [t.keyword, t.operator, t.tagName],
      color: '#ff79c6',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    { tag: [t.function(t.propertyName), t.propertyName], color: '#66d9ef' },
    {
      tag: [
        t.definition(t.variableName),
        t.function(t.variableName),
        t.className,
        t.attributeName,
      ],
      color: '#50fa7b',
    },
    { tag: t.regexp, color: '#ffb86c', fontWeight: 'bold' },
    { tag: t.paren, color: '#555' },
    { tag: filterTag, color: '#6272a4', fontStyle: 'italic' },
  ]),
);

export const sensikaDarkTheme = [darkUITheme, darkSyntaxHighlighting];

// ==================== LIGHT THEME ====================

const lightUITheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#ffffff',
      color: '#24292e',
    },
    '.cm-content': {
      caretColor: '#24292e',
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: '#24292e',
    },
    '.cm-activeLine': {
      backgroundColor: 'unset',
    },
    '&.cm-focused .cm-selectionBackground, & .cm-line::selection, & .cm-selectionLayer .cm-selectionBackground, .cm-content ::selection':
      {
        background: '#b3d7ff !important',
      },
    '& .cm-selectionMatch': {
      backgroundColor: '#c8e1ff',
    },
    '.cm-gutters': {
      backgroundColor: '#f6f8fa',
      color: '#6a737d',
      borderRight: '1px solid #e1e4e8',
    },
    '.cm-activeLineGutter': {
      backgroundColor: '#cad4dd',
    },
    '.cm-matchingBracket': {
      backgroundColor: 'rgba(255, 255, 0, 0.5)',
      outline: '1px solid #ffc107',
    },
    '&.cm-focused .cm-matchingBracket': {
      backgroundColor: 'rgba(255, 255, 0, 0.5)',
      outline: '1px solid #ffc107',
    },
    '.cm-query-dimension': {
      color: '#999 !important',
      fontWeight: 'bold',
    },
    '.cm-query-dimension span': {
      color: '#999 !important',
      fontWeight: 'bold',
    },
    '.cm-query-property-name': {
      color: '#001cc5',
      fontWeight: 'bold',
    },
    '.cm-query-property-name span': {
      color: '#001cc5',
      fontWeight: 'bold',
    },
    '.cm-searchMatch': {
      backgroundColor: 'rgba(255, 165, 0, 0.5)',
    },
    '.cm-searchMatch-selected': {
      backgroundColor: 'rgba(255, 0, 191, 0.5)', // active match
    },
  },
  { dark: false },
);

const lightSyntaxHighlighting = syntaxHighlighting(
  HighlightStyle.define([
    { tag: t.comment, color: '#6a737d' },
    { tag: t.lineComment, color: '#6a737d' },
    {
      tag: t.blockComment,
      color: '#0080ff',
      fontWeight: 'bold',
      fontStyle: 'italic',
    },
    { tag: t.string, color: 'red' },
    { tag: t.atom, color: '#005cc5' },
    { tag: t.meta, color: '#24292e' },
    {
      tag: [t.keyword, t.operator, t.tagName],
      color: '#708',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    { tag: [t.function(t.propertyName), t.propertyName], color: '#005cc5' },
    {
      tag: [
        t.definition(t.variableName),
        t.function(t.variableName),
        t.className,
        t.attributeName,
      ],
      color: '#22863a',
    },
    { tag: t.regexp, color: '#be0000', fontWeight: 'bold' },
    { tag: t.paren, color: '#555' },
    { tag: filterTag, color: '#999', fontStyle: 'italic' },
  ]),
);

export const sensikaLightTheme = [lightUITheme, lightSyntaxHighlighting];
